import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = [
    "chatMessages",
    "messageInput",
    "sendButton",
    "canvasContent",
    "goalInput",
    "questionsList",
    "saveButton",
    "saveAndTestButton",
    "recruitPersona",
    "recruitChannels",
    "recruitQuotaBasic",
    "recruitQuotaPro",
    "recruitmentSection",
    "outlineTabContent",
    "recruitTabContent",
    "guideTabContent",
    "personaTabContent",
    "personaGuideWrapper",
    "inspectorResult",
    "diagnosisCard",
    "diagStage",
    "diagMethod"
  ]

  static values = {
    interviewSetId: Number,
    researchTopicTitle: String,
    researchBackground: String,
    modeRequirements: Object,
    followUpsPerMain: { type: Number, default: 3 },
    existingDesignData: { type: Object, default: {} },
    existingQuestions: { type: Array, default: [] }
  }

  connect() {
    console.log("ResearchDesignController connected")

    // Initialize state
    this.appState = 'DISCOVERY'
    this.configContext = ""
    this.targetMainQCount = 0
    this.selectedMethod = ""
    this.diagnosisState = {
      stage: null,
      method: [],
      confidence: 0,
      gaps: "",
      goal: ""
    }
    this.currentOutline = {
      goal: "",
      stage: "",
      method: "",
      recruitment: null,
      questions: [],
      goalSuggestion: null,
      personaGuide: null
    }
    this.acceptedSuggestions = []
    this.conversation = [
      { role: "system", content: this.getBasePersona() }
    ]
    this.hasUnsavedChanges = false
    this.questionCountConfirmed = false

    this.modeRequirements = this.normalizeModeRequirements(this.modeRequirementsValue)

    // Subscribe to ActionCable
    this.setupCable()

    // Add beforeunload listener to warn about unsaved changes
    this.beforeUnloadHandler = (e) => {
      if (this.hasUnsavedChanges && this.currentOutline.questions && this.currentOutline.questions.length > 0) {
        const message = '您有未保存的更改，离开页面将丢失这些更改。确定要离开吗？'
        e.preventDefault()
        e.returnValue = message
        return message
      }
    }
    window.addEventListener('beforeunload', this.beforeUnloadHandler)

    // Try to restore from existing design data
    const hasExistingData = this.restoreExistingDesign()

    // Render initial UI - start with discovery question
    if (!hasExistingData) {
      this.renderDiscoveryWelcome()
    }
  }

  // Restore existing design data if available
  restoreExistingDesign() {
    const designData = this.existingDesignDataValue
    const existingQuestions = this.existingQuestionsValue

    console.log('Restoring design data:', designData)
    console.log('Existing questions:', existingQuestions)

    // Check if we have existing questions
    if (!existingQuestions || existingQuestions.length === 0) {
      return false
    }

    // Restore questions
    this.currentOutline.questions = existingQuestions

    // Restore design configuration if available
    if (designData.method && designData.target_question_count) {
      // Has complete design data - restore everything
      this.selectedMethod = designData.method
      this.targetMainQCount = designData.target_question_count
      this.configContext = designData.config_context || ""
      this.currentOutline.goal = designData.goal || ""
      this.currentOutline.stage = designData.stage || ""
      this.currentOutline.method = designData.diagnosis_method || ""
      this.currentOutline.recruitment = designData.recruitment || null
      this.currentOutline.personaGuide = designData.persona_guide || null
      this.appState = 'ITERATE'
      this.questionCountConfirmed = true

      console.log('Restored recruitment data:', this.currentOutline.recruitment)

      // Show restoration message
      this.renderChat({
        role: "assistant",
        content: `
          <div style="background: #d1f4ff; border: 1px solid #0ea5e9; border-radius: 8px; padding: 12px; margin: 8px 0; color: #0c4a6e;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
              </svg>
              <span style="font-weight: 600;">已恢复现有设计</span>
            </div>
            <div style="font-size: 13px; line-height: 1.5;">
              研究模式：${this.formatMethodName(this.selectedMethod)}<br>
              目标问题数：${this.targetMainQCount} 个<br>
              当前问题数：${existingQuestions.length} 个
            </div>
          </div>
          您可以继续修改提纲，或者直接保存使用。
        `
      })

      // Render the canvas with existing data
      this.renderCanvas(this.currentOutline)

      // Auto-resize recruitment textareas after rendering
      setTimeout(() => {
        if (this.hasRecruitPersonaTarget) {
          this.recruitPersonaTarget.style.height = 'auto'
          this.recruitPersonaTarget.style.height = this.recruitPersonaTarget.scrollHeight + 'px'
        }
        if (this.hasRecruitChannelsTarget) {
          this.recruitChannelsTarget.style.height = 'auto'
          this.recruitChannelsTarget.style.height = this.recruitChannelsTarget.scrollHeight + 'px'
        }
      }, 100)

      return true
    } else {
      // Has questions but missing config - show question count selector
      this.currentOutline.goal = designData.goal || ""
      this.currentOutline.recruitment = designData.recruitment || null

      // Show message
      this.renderChat({
        role: "assistant",
        content: `
          <div style="background: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px; padding: 12px; margin: 8px 0; color: #7c2d12;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
              <span style="font-weight: 600;">发现已有 ${existingQuestions.length} 个问题</span>
            </div>
            <div style="font-size: 13px;">
              请确认目标问题数量以继续设计。
            </div>
          </div>
        `
      })

      // Render existing questions on canvas
      this.renderCanvas(this.currentOutline)

      // Auto-resize recruitment textareas after rendering
      setTimeout(() => {
        if (this.hasRecruitPersonaTarget) {
          this.recruitPersonaTarget.style.height = 'auto'
          this.recruitPersonaTarget.style.height = this.recruitPersonaTarget.scrollHeight + 'px'
        }
        if (this.hasRecruitChannelsTarget) {
          this.recruitChannelsTarget.style.height = 'auto'
          this.recruitChannelsTarget.style.height = this.recruitChannelsTarget.scrollHeight + 'px'
        }
      }, 100)

      // Show question count selector directly
      this.showQuestionCountSelector()

      return true
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }

    // Clean up beforeunload listener
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    }
  }

  setupCable() {
    const consumer = createConsumer()
    
    this.subscription = consumer.subscriptions.create(
      {
        channel: "ResearchDesignChannel",
        interview_set_id: this.interviewSetIdValue
      },
      {
        connected: () => {
          console.log("Connected to ResearchDesignChannel")
        },
        
        disconnected: () => {
          console.log("Disconnected from ResearchDesignChannel")
        },
        
        received: (data) => {
          this.handleChannelMessage(data)
        }
      }
    )
  }

  // Handle messages from ActionCable
  handleChannelMessage(data) {
    console.log("Received:", data)
    
    switch (data.type) {
      case "stream":
        this.handleStreamChunk(data.content)
        break
      
      case "complete":
        this.handleComplete(data)
        break
      
      case "error":
        this.handleError(data.message)
        break
      
      case "save_complete":
        this.handleSaveComplete(data)
        break

      case "inspect_question_complete":
        this.handleInspectQuestionComplete(data)
        break

      case "persona_guide_complete":
        this.handlePersonaGuideComplete(data)
        break
    }
  }

  handleStreamChunk(content) {
    // For now, we'll collect the stream but not display it incrementally
    // The complete message will have the full response
  }

  handleComplete(data) {
    this.removeTypingIndicator()
    
    if (data.reply) {
      this.processAndRender(data.reply, true)
    }
    
    // Re-enable input
    this.sendButtonTarget.disabled = false
    this.sendButtonTarget.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"></line>
        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
      </svg>
    `
  }

  handleError(message) {
    this.removeTypingIndicator()
    this.renderChat({ 
      role: "assistant", 
      content: `<span style="color:var(--danger-color)">出错: ${message}</span>` 
    })
    
    // Re-enable input
    this.sendButtonTarget.disabled = false
    this.sendButtonTarget.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"></line>
        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
      </svg>
    `
  }

  // Send message
  sendMessage() {
    if (this.sendButtonTarget.disabled) return
    const text = this.messageInputTarget.value.trim()
    if (!text) return
    
    // Display user message
    const userMsgForUI = { role: "user", content: text }
    this.renderChat(userMsgForUI)
    this.showTypingIndicator()
    
    // Add to conversation history
    this.conversation.push({ role: "user", content: text })
    
    // Clear input
    this.messageInputTarget.value = ''
    this.messageInputTarget.style.height = '22px'
    
    // Disable button
    this.sendButtonTarget.disabled = true
    this.sendButtonTarget.innerHTML = '<span style="font-size:14px;">...</span>'
    
    // Prepare system prompt
    let currentSystemPrompt = this.getBasePersona()
    
    if (this.appState !== 'DISCOVERY' || this.configContext) {
      currentSystemPrompt += "\n" + this.configContext
    }
    
    switch (this.appState) {
      case 'DISCOVERY':
        currentSystemPrompt += "\n" + this.getPromptDiscovery()
        break
      case 'INITIAL':
        currentSystemPrompt += "\n" + this.getPromptInitial()
        break
      case 'ITERATE':
        currentSystemPrompt += "\n" + this.getPromptIterate()
        break
    }
    
    // Prepare messages payload
    let messagesPayload = JSON.parse(JSON.stringify(this.conversation))
    messagesPayload[0] = { role: "system", content: currentSystemPrompt }
    
    // Add context if in ITERATE mode
    if (this.appState === 'ITERATE' && this.currentOutline.questions && this.currentOutline.questions.length > 0) {
      const cleanedOutline = this.clearAllSuggestions(this.currentOutline)
      const contextData = this.getUpdateContext(cleanedOutline)
      messagesPayload[messagesPayload.length - 1].content += "\n\n" + contextData
    }
    
    // Send via ActionCable
    this.subscription.send({
      action: "design_message",
      messages: messagesPayload
    })
  }

  // Handle enter key
  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  // Auto-resize textarea
  autoResizeTextarea(event) {
    const el = event.target
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }

  normalizeModeRequirements(rawRequirements) {
    if (!rawRequirements || typeof rawRequirements !== 'object') {
      return this.getFallbackModeRequirements()
    }

    const normalized = {}
    Object.entries(rawRequirements).forEach(([mode, requirement]) => {
      if (typeof mode === 'string' && mode.trim() && typeof requirement === 'string' && requirement.trim()) {
        normalized[mode] = requirement.trim()
      }
    })

    if (Object.keys(normalized).length > 0) {
      return normalized
    }

    return this.getFallbackModeRequirements()
  }

  getFallbackModeRequirements() {
    return {
      "通用模板": [
        "【执行指令 - 通用模式】：",
        "1. 根据用户描述灵活设计，保持逻辑严密。",
        "2. 提纲结构包含：背景、过程、评价。"
      ].join("\n")
    }
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }
    return String(text).replace(/[&<>"']/g, (ch) => map[ch])
  }

  // Process AI response and render
  processAndRender(rawText, saveToHistory = false) {
    this.removeTypingIndicator()

    // Strip non-essential tags
    rawText = rawText.replace(/<think>[\s\S]*?<\/think>/g, '')

    // Extract <diagnosis> tag
    const diagMatch = rawText.match(/<diagnosis>([\s\S]*?)<\/diagnosis>/)
    if (diagMatch) {
      try {
        const diagData = JSON.parse(diagMatch[1].trim())
        this.diagnosisState = { ...this.diagnosisState, ...diagData }
        // Update canvas diagnosis display
        if (diagData.stage) this.currentOutline.stage = diagData.stage
        if (diagData.method) {
          this.currentOutline.method = Array.isArray(diagData.method) ? diagData.method : [diagData.method]
        }
        if (diagData.goal) this.currentOutline.goal = diagData.goal
      } catch (e) {
        console.warn("Diagnosis parse failed:", e)
      }
      rawText = rawText.replace(/<diagnosis>[\s\S]*?<\/diagnosis>/g, '')
    }

    rawText = rawText.replace(/<persona>[\s\S]*?<\/persona>/g, '')
    rawText = rawText.replace(/<persona_guide>[\s\S]*?<\/persona_guide>/g, '')

    // Extract <outline> tag
    const outlineMatch = rawText.match(/<outline>([\s\S]*?)<\/outline>/)

    // Extract <reply> tag
    const replyMatch = rawText.match(/<reply>([\s\S]*?)<\/reply>/)

    let chatText = rawText
    let outlineData = null
    let hasValidOutline = false

    if (outlineMatch) {
      try {
        outlineData = JSON.parse(outlineMatch[1].trim())
        hasValidOutline = true

        // Handle legacy format
        if (outlineData.mainQuestions && !outlineData.questions) {
          outlineData.questions = outlineData.interview_guide_outline.map(q =>
            typeof q === 'string' ? {text: q, strategy: "深入挖掘"} : q
          )
        }

        // Merge AI questions with existing: preserve strategy/followUpCount for unchanged questions
        if (outlineData.questions) {
          const existingQuestions = this.currentOutline.questions || []
          this.currentOutline.questions = outlineData.questions.map((newQ, idx) => {
            const existing = existingQuestions[idx]
            if (!existing) {
              // New question added by AI
              return {
                text: newQ.text || "",
                strategy: newQ.strategy || "深入挖掘具体细节和感受",
                followUpCount: newQ.followUpCount ?? this.followUpsPerMainValue,
                ...(newQ.suggestion ? { suggestion: newQ.suggestion } : {})
              }
            }
            return {
              text: newQ.text || existing.text,
              strategy: newQ.strategy || existing.strategy || "深入挖掘具体细节和感受",
              followUpCount: newQ.followUpCount ?? existing.followUpCount ?? this.followUpsPerMainValue,
              ...(newQ.suggestion ? { suggestion: newQ.suggestion } : {})
            }
          })
        }

        // Handle goal suggestion
        if (outlineData.goalSuggestion) {
          if (typeof outlineData.goalSuggestion === 'object' && outlineData.goalSuggestion.new_goal) {
            this.currentOutline.goalSuggestion = outlineData.goalSuggestion
          }
        }

        // Update goal if present
        if (outlineData.goal) {
          this.currentOutline.goal = outlineData.goal
        }

        // Update diagnosis fields if present
        if (outlineData.stage) {
          this.currentOutline.stage = outlineData.stage
        }
        if (outlineData.method) {
          this.currentOutline.method = outlineData.method
        }

        // Update persona guide if present
        if (outlineData.persona_guide) {
          this.currentOutline.personaGuide = outlineData.persona_guide
        }

        // Handle recruitment suggestion (do not merge directly)
        if (outlineData.recruitmentSuggestion) {
          this.currentOutline.recruitmentSuggestion = outlineData.recruitmentSuggestion
        }

        // Merge recruitment data (only overwrite if AI returned valid content)
        if (outlineData.recruitment) {
          const normalizedRecruitment = this.normalizeRecruitmentData(outlineData.recruitment)
          if (normalizedRecruitment) {
            if (!this.currentOutline.recruitment) this.currentOutline.recruitment = {}
            const newR = normalizedRecruitment
            const oldR = this.currentOutline.recruitment
            if (newR.persona && newR.persona.trim() !== "") oldR.persona = newR.persona
            if (newR.channels && Array.isArray(newR.channels) && newR.channels.length > 0 && newR.channels[0] !== "") oldR.channels = newR.channels
            if (newR.quota && (Array.isArray(newR.quota.basic) || Array.isArray(newR.quota.pro))) oldR.quota = newR.quota
          }
        }

        // Render canvas (all data already merged into this.currentOutline)
        this.renderCanvas(null)

        // Mark as having unsaved changes
        this.hasUnsavedChanges = true

        // State transition logic
        if (this.appState === 'INITIAL' && outlineData.questions && outlineData.questions.length > 0) {
          this.appState = 'ITERATE'
        }

        // Auto-generate persona guide only when missing
        if (!this.currentOutline.personaGuide && this.currentOutline.recruitment?.persona && this.currentOutline.goal) {
          this.generatePersonaGuide()
        }

      } catch (e) {
        console.error("❌ 提纲 JSON 解析失败:", e)
        hasValidOutline = false
      }
    }

    // Prepare chat text - only show the reply content
    if (replyMatch) {
      // If there's a reply tag, use only its content
      chatText = replyMatch[1].trim()
    } else if (hasValidOutline) {
      // If outline was processed successfully but no reply tag, extract non-outline text
      chatText = rawText
        .replace(/<outline>[\s\S]*?<\/outline>/, '')
        .replace(/<\/?reply>/g, '')
        .trim()

      // If chatText is empty or looks like JSON, provide a default message
      if (!chatText || chatText.startsWith('{') || chatText.startsWith('[')) {
        chatText = '提纲已更新'
      }
    } else {
      // No valid outline and no reply tag - check if response is mostly JSON
      const jsonPattern = /^\s*[\{\[]/
      if (jsonPattern.test(rawText.trim())) {
        // Response starts with JSON - likely formatting error
        console.warn("⚠️ AI response appears to be raw JSON without proper tags")
        chatText = '<span style="color:var(--danger-color);">AI 响应格式异常，请重试</span>'
      } else {
        // Keep the original text but remove any obvious JSON blocks
        chatText = rawText
          .replace(/\{[\s\S]*?\}/g, '') // Remove JSON objects
          .replace(/\[[\s\S]*?\]/g, '') // Remove JSON arrays
          .trim()

        if (!chatText) {
          chatText = '<span style="color:var(--text-secondary);">收到响应，但无可显示内容</span>'
        }
      }
    }

    // Add error indicator if outline parsing failed
    if (outlineMatch && !hasValidOutline) {
      chatText += ` <span style="color:var(--danger-color); font-size:12px; margin-left: 8px;">(提纲解析失败)</span>`
    }

    // Render chat message
    this.renderChat({
      role: "assistant",
      content: chatText + (hasValidOutline ? `<br><div class="sync-card"><svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path></svg> 画板上的提纲已同步更新</div>` : '')
    })

    if (saveToHistory) {
      this.conversation.push({ role: "assistant", content: chatText })
    }

    // In DISCOVERY state, show question count selector after AI responds with diagnosis
    if (this.appState === 'DISCOVERY' && !this.questionCountConfirmed && diagMatch) {
      setTimeout(() => this.showQuestionCountSelector(), 300)
    }
  }

  // Render chat message
  renderChat(msg) {
    const box = this.chatMessagesTarget
    
    const row = document.createElement('div')
    row.className = 'message-row'
    
    const avatar = document.createElement('div')
    avatar.className = `avatar ${msg.role === 'assistant' ? 'ai' : 'user'}`
    avatar.innerText = msg.role === 'assistant' ? '🤖' : '👤'
    
    const bubble = document.createElement('div')
    bubble.className = `bubble ${msg.role === 'assistant' ? 'ai' : 'user'}`
    bubble.innerHTML = msg.content
    
    if (msg.role === 'user') {
      row.appendChild(bubble)
      row.style.justifyContent = 'flex-end'
    } else {
      row.appendChild(avatar)
      row.appendChild(bubble)
    }
    
    box.appendChild(row)
    box.scrollTop = box.scrollHeight
  }

  // Show typing indicator
  showTypingIndicator() {
    if (document.getElementById('typing-indicator')) return
    
    const row = document.createElement('div')
    row.className = 'message-row'
    row.id = 'typing-indicator'
    
    const avatar = document.createElement('div')
    avatar.className = 'avatar ai'
    avatar.innerText = '🤖'
    
    const bubble = document.createElement('div')
    bubble.className = 'bubble ai'
    bubble.innerHTML = `<div class="typing-indicator"><span></span><span></span><span></span></div>`
    
    row.appendChild(avatar)
    row.appendChild(bubble)
    this.chatMessagesTarget.appendChild(row)
    this.chatMessagesTarget.scrollTop = this.chatMessagesTarget.scrollHeight
  }

  removeTypingIndicator() {
    const indicator = document.getElementById('typing-indicator')
    if (indicator) indicator.remove()
  }

  // Render discovery welcome message
  renderDiscoveryWelcome() {
    const topicTitle = this.researchTopicTitleValue || ""
    const background = this.researchBackgroundValue || ""
    
    let welcomeContent = `你好！我是你的研究搭档。`
    if (topicTitle) {
      welcomeContent += `我已了解本次研究主题：<b>${topicTitle}</b>`
      if (background) {
        welcomeContent += `，以及相关背景信息。`
      } else {
        welcomeContent += `。`
      }
    }
    welcomeContent += `<br><br><b>你们目前业务处于什么阶段？</b>（比如早期探索、准备上线、还是已经运营一段时间了？）`
    
    this.conversation.push({ role: "assistant", content: welcomeContent })
    this.renderChat({ role: "assistant", content: welcomeContent })
  }

  // Show question count selector (after AI auto-detects mode)
  showQuestionCountSelector() {
    const box = this.chatMessagesTarget
    const selectorDiv = document.createElement('div')
    selectorDiv.className = 'method-select-box'
    selectorDiv.style.marginLeft = '44px'
    
    const options = Array.from({ length: 15 }, (_, i) => {
      const n = i + 1
      const estimatedMinutes = Math.round(n * (1 + this.followUpsPerMainValue) * 1.5)
      return `<option value="${n}"${n === 5 ? ' selected' : ''}>${n} 个主问题（约 ${estimatedMinutes} 分钟）</option>`
    }).join('')
    
    selectorDiv.innerHTML = `
      <div style="font-size:13px; color:var(--text-primary); margin-bottom:10px; font-weight:700;">📝 请设置目标主问题数量：</div>
      <select id="question-count-dropdown" class="custom-select">
        ${options}
      </select>
      <button class="select-confirm-btn" data-action="click->interview-design#confirmQuestionCount">确定问题数量</button>
    `
    
    box.appendChild(selectorDiv)
    box.scrollTop = box.scrollHeight
  }

  // Confirm question count
  confirmQuestionCount(event) {
    const select = this.element.querySelector('#question-count-dropdown')
    const questionCount = parseInt(select.value)
    this.targetMainQCount = questionCount
    this.questionCountConfirmed = true
    const estimatedMinutes = Math.round(questionCount * (1 + this.followUpsPerMainValue) * 1.5)
    
    // Auto-detect method from diagnosis or use fallback
    const methodKey = (this.diagnosisState.method && this.diagnosisState.method.length > 0)
      ? this.diagnosisState.method[0]
      : (this.selectedMethod || "通用模板")
    this.selectedMethod = methodKey
    
    const requirement = this.modeRequirements[methodKey] || ""
    this.configContext = `
【用户配置已锁定】：
- 研究模式：${this.formatMethodName(methodKey)}（AI 自动推荐）
- 数量约束：严格控制在 ${questionCount} 个主问题
- 方法论指导：${requirement}
`
    
    const confirmMsg = `
      <div style="font-weight:700; color:var(--accent-color); margin-bottom:6px; font-size:14px; display:flex; align-items:center; gap:6px;">
        <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        问题数量已锁定
      </div>
      <div style="font-size:12px; color:var(--text-secondary); margin-bottom:12px; background:#f8fafc; border: 1px solid var(--border-color); padding:6px 10px; border-radius:6px;">
        📝 目标主问题数：${questionCount}个 &nbsp;|&nbsp; ⏱️ 预计时长：约 ${estimatedMinutes} 分钟 &nbsp;|&nbsp; 🧠 模式：${this.formatMethodName(methodKey)}
      </div>
      <div style="line-height: 1.5;">您可以继续与 AI 对话来完善提纲，或者直接编辑右侧画板上的内容。</div>
    `
    this.renderChat({ role: "assistant", content: confirmMsg })

    // Remove the selector box
    event.target.closest('.method-select-box').remove()

    // Trigger initial outline generation
    this.appState = 'INITIAL'
    this.showTypingIndicator()
    
    this.conversation.push({ role: "user", content: "开始生成提纲" })
    
    this.sendButtonTarget.disabled = true
    this.sendButtonTarget.innerHTML = '<span style="font-size:14px;">...</span>'
    
    let currentSystemPrompt = this.getBasePersona()
    currentSystemPrompt += "\n" + this.configContext
    currentSystemPrompt += "\n" + this.getPromptInitial()
    
    let messagesPayload = JSON.parse(JSON.stringify(this.conversation))
    messagesPayload[0] = { role: "system", content: currentSystemPrompt }
    
    this.subscription.send({
      action: "design_message",
      messages: messagesPayload
    })
  }

  // Calculate question limits
  calculateQuestionLimits(minutes) {
    const timePerQuestion = 1.5
    const totalQuestions = Math.floor(minutes / timePerQuestion)
    const mainQuestions = Math.floor(totalQuestions / (1 + this.followUpsPerMainValue))
    return { totalQuestions, mainQuestions }
  }

  // Render canvas - always renders from this.currentOutline
  renderCanvas() {
    // Clean invalid suggestions (only remove if BOTH text and strategy are unchanged)
    if (this.currentOutline.questions) {
      this.currentOutline.questions.forEach(q => {
        if (q.suggestion && q.suggestion.action !== 'delete') {
          const suggestionText = q.suggestion.new_text || q.suggestion.text
          const suggestionStrategy = q.suggestion.new_strategy || q.suggestion.strategy
          const textUnchanged = !suggestionText || suggestionText === q.text
          const strategyUnchanged = !suggestionStrategy || suggestionStrategy === q.strategy
          if (textUnchanged && strategyUnchanged) {
            delete q.suggestion
          }
        }
      })
    }
    
    const displayGoal = this.currentOutline.goal || ""
    const goalEl = this.goalInputTarget
    
    // Remove old goal suggestion
    const goalWrapper = goalEl.closest('.diagnosis-card')
    const oldSugg = goalWrapper.querySelector('.suggestion-box')
    if (oldSugg) oldSugg.remove()

    // Render diagnosis fields (stage, method)
    if (this.hasDiagStageTarget) {
      const stageVal = this.currentOutline.stage || ""
      this.diagStageTarget.innerHTML = stageVal
        ? `<span class="diagnosis-tag">${stageVal}</span>`
        : '待分析…'
    }
    if (this.hasDiagMethodTarget) {
      const methodVal = this.currentOutline.method || ""
      if (methodVal) {
        const methods = Array.isArray(methodVal) ? methodVal : [methodVal]
        this.diagMethodTarget.innerHTML = methods.map(m => `<span class="diagnosis-tag">${m}</span>`).join('')
      } else {
        this.diagMethodTarget.innerHTML = '待分析…'
      }
    }
    
    // Render goal suggestion if present
    if (this.currentOutline.goalSuggestion) {
      const sugg = this.currentOutline.goalSuggestion
      const suggDiv = document.createElement('div')
      suggDiv.className = 'suggestion-box'
      suggDiv.style.marginTop = '12px'
      suggDiv.innerHTML = `
        <div class="suggestion-header">
          <span>💡 AI 建议修改研究目标</span>
          <div class="btn-group">
            <button class="accept-btn" data-action="click->interview-design#resolveGoalSuggestion" data-accept="true">接受</button>
            <button class="reject-btn" data-action="click->interview-design#resolveGoalSuggestion" data-accept="false">拒绝</button>
          </div>
        </div>
        <div class="suggestion-text">
          <strong>新目标：</strong>${sugg.new_goal}
          ${sugg.reason ? `<div style="margin-top:8px; font-size:12px; opacity:0.9; border-top:1px dashed rgba(6,78,59,0.2); padding-top:8px;">💡 理由：${sugg.reason}</div>` : ''}
        </div>
      `
      goalWrapper.appendChild(suggDiv)
    }
    
    if (displayGoal) {
      goalEl.value = displayGoal
      setTimeout(() => {
        goalEl.style.height = 'auto'
        goalEl.style.height = goalEl.scrollHeight + 'px'
      }, 0)
    }

    // --- Render recruitment section ---
    if (this.hasRecruitmentSectionTarget) {
      const recruitSection = this.recruitmentSectionTarget

      // Remove old recruitment suggestion box
      const oldRecruitSugg = recruitSection.querySelector('.suggestion-box')
      if (oldRecruitSugg) oldRecruitSugg.remove()

      // Render new recruitment suggestion if present
      if (this.currentOutline.recruitmentSuggestion) {
        this._applyTab('recruit') // auto-switch to show suggestion
        const sugg = this.currentOutline.recruitmentSuggestion
        let changesHtml = ''
        if (sugg.persona) changesHtml += `<li><strong>\u753b\u50cf\u66f4\u65b0:</strong> ${sugg.persona.substring(0, 80)}...</li>`
        if (sugg.channels) changesHtml += `<li><strong>\u6e20\u9053\u66f4\u65b0:</strong> ${Array.isArray(sugg.channels) ? sugg.channels.join('; ') : sugg.channels}</li>`
        if (sugg.quota) changesHtml += `<li><strong>\u914d\u989d\u66f4\u65b0:</strong> \u7ed3\u6784\u5df2\u91cd\u65b0\u8c03\u6574</li>`
        const suggDiv = document.createElement('div')
        suggDiv.className = 'suggestion-box'
        suggDiv.style.marginBottom = '12px'
        suggDiv.innerHTML = `
          <div class="suggestion-header">
            <span>\ud83d\udca1 AI \u5efa\u8bae\u4fee\u6539\u62db\u52df\u8ba1\u5212</span>
            <div class="btn-group">
              <button class="accept-btn" data-action="click->interview-design#resolveRecruitmentSuggestion" data-accept="true">\u63a5\u53d7</button>
              <button class="reject-btn" data-action="click->interview-design#resolveRecruitmentSuggestion" data-accept="false">\u62d2\u7edd</button>
            </div>
          </div>
          <div class="suggestion-text">
            <ul style="margin:4px 0 0 0; padding-left:20px; font-size:13px;">${changesHtml}</ul>
            ${sugg.reason ? `<div style="margin-top:8px; font-size:12px; opacity:0.9; border-top:1px dashed rgba(6,78,59,0.2); padding-top:8px;">\ud83d\udca1 \u7406\u7531\uff1a${sugg.reason}</div>` : ''}
          </div>
        `
        recruitSection.insertBefore(suggDiv, recruitSection.firstChild)
      }

      // Populate recruitment fields if data exists
      if (this.currentOutline.recruitment) {
        const r = this.currentOutline.recruitment

        if (this.hasRecruitPersonaTarget) {
          this.recruitPersonaTarget.value = r.persona || ''
        }

        if (this.hasRecruitChannelsTarget) {
          this.recruitChannelsTarget.value = Array.isArray(r.channels) ? r.channels.join('\n') : (r.channels || '')
        }

        if (this.hasRecruitQuotaBasicTarget && r.quota && Array.isArray(r.quota.basic)) {
          const quotaBasicEl = this.recruitQuotaBasicTarget
          quotaBasicEl.innerHTML = ''
          r.quota.basic.forEach((q, idx) => {
            quotaBasicEl.innerHTML += `
              <div class="quota-row">
                <span style="font-size: 14px;">\ud83d\udc64</span>
                <input class="quota-profile" value="${q.profile || ''}" data-index="${idx}" data-field="profile" data-action="change->interview-design#updateQuotaBasic" placeholder="\u4eba\u7fa4\u63cf\u8ff0..." />
                <input class="quota-count" type="number" value="${parseInt(q.count) || 0}" data-index="${idx}" data-field="count" data-action="change->interview-design#updateQuotaBasic" min="0" />
                <span style="font-size: 12px; color: var(--text-secondary);">%</span>
              </div>
            `
          })
        }

        if (this.hasRecruitQuotaProTarget && r.quota && Array.isArray(r.quota.pro)) {
          const quotaProEl = this.recruitQuotaProTarget
          quotaProEl.innerHTML = ''
          r.quota.pro.forEach((dim, dimIdx) => {
            let dimHtml = `
              <div class="quota-row" style="display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 8px 10px;">
                <div style="font-size: 13px; font-weight: 700; color: var(--primary-color); min-width: 60px;">\ud83d\udccc ${dim.dimension}</div>
                <div style="display: flex; flex-wrap: wrap; gap: 8px; flex: 1;">`
            if (Array.isArray(dim.segments)) {
              dim.segments.forEach((seg, segIdx) => {
                dimHtml += `
                  <div style="display: flex; align-items: center; gap: 4px; background: #f1f5f9; padding: 4px 8px; border-radius: 4px; border: 1px solid var(--border-color);">
                    <input class="quota-profile" style="width: 70px; flex: none; font-size: 12px; background: transparent; padding: 0;" value="${seg.name || ''}" data-dim-index="${dimIdx}" data-seg-index="${segIdx}" data-field="name" data-action="change->interview-design#updateQuotaPro" placeholder="\u5206\u7c7b..." />
                    <input class="quota-count" style="width: 45px; font-size: 12px; padding: 2px 4px; background: #ffffff; border: 1px solid #cbd5e1;" type="number" value="${parseInt(seg.count) || 0}" data-dim-index="${dimIdx}" data-seg-index="${segIdx}" data-field="count" data-action="change->interview-design#updateQuotaPro" min="0" />
                    <span style="font-size: 12px; color: var(--text-secondary);">%</span>
                  </div>`
              })
            }
            dimHtml += `</div></div>`
            quotaProEl.innerHTML += dimHtml
          })
        }
      }
    }

    // --- Render persona guide tab ---
    if (this.hasPersonaGuideWrapperTarget) {
      this.renderPersonaGuide()
    }

    const list = this.questionsListTarget
    list.innerHTML = ''
    
    if (this.currentOutline.questions && this.currentOutline.questions.length > 0) {
      this.currentOutline.questions.forEach((q, idx) => {
        const card = document.createElement('div')
        card.className = 'question-card'
        
        let finalHtml = `
          <div class="q-id">Q${idx + 1}</div>
          <div class="q-content">
            <textarea class="q-text-input" 
                      data-index="${idx}"
                      data-action="input->interview-design#autoResizeTextarea change->interview-design#updateQuestionText">${q.text}</textarea>
            
            <div class="q-meta-group">
              <div class="q-strategy-row">
                <span class="q-strategy-label">要求</span>
                <textarea class="q-strategy-input"
                          data-index="${idx}"
                          data-action="input->interview-design#autoResizeTextarea change->interview-design#updateQuestionStrategy">${q.strategy}</textarea>
              </div>
              <div class="q-followup-row">
                <span class="q-strategy-label">追问次数</span>
                <input type="number" class="q-followup-input" min="0" value="${q.followUpCount}" 
                       data-index="${idx}"
                       data-action="change->interview-design#updateQuestionFollowUp" />
              </div>
            </div>`
        
        if (q.suggestion) {
          const isDelete = q.suggestion.action === 'delete'
          finalHtml += `
            <div class="suggestion-box" style="${isDelete ? 'background:#fef2f2; border-color:#fca5a5;' : ''}">
              <div class="suggestion-header">
                <span style="${isDelete ? 'color:#991b1b;' : ''}">💡 AI ${isDelete ? '移除建议' : '修改建议'}</span>
                <div class="btn-group">
                  <button class="accept-btn" style="${isDelete ? 'background:var(--danger-color);' : ''}" 
                          data-action="click->interview-design#resolveSuggestion"
                          data-index="${idx}" data-accept="true">确认${isDelete ? '删除' : '接受'}</button>
                  <button class="reject-btn" 
                          data-action="click->interview-design#resolveSuggestion"
                          data-index="${idx}" data-accept="false">拒绝</button>
                </div>
              </div>
              <div class="suggestion-text" style="${isDelete ? 'color:#991b1b; background: rgba(239, 68, 68, 0.05);' : ''}">
                ${isDelete ? 
                  `<strong>理由：</strong>${q.suggestion.text || q.suggestion.reason}` : 
                  `<strong>新问题：</strong>${q.suggestion.new_text || q.suggestion.text}`
                }
                ${q.suggestion.reason && !isDelete ? `<div style="margin-top:8px; font-size:12px; opacity:0.9; border-top: 1px dashed rgba(6, 78, 59, 0.2); padding-top: 8px;">💡 理由：${q.suggestion.reason}</div>` : ''}
              </div>
            </div>`
        }
        
        finalHtml += `</div>
          <div class="q-actions">
            <button class="action-btn" data-action="click->interview-design#moveQuestionUp" data-index="${idx}" title="上移">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 15l-6-6-6 6"/></svg>
            </button>
            <button class="action-btn" data-action="click->interview-design#moveQuestionDown" data-index="${idx}" title="下移">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            <button class="action-btn del" data-action="click->interview-design#deleteQuestion" data-index="${idx}" title="删除">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>`
        
        card.innerHTML = finalHtml
        list.appendChild(card)
      })
      
      // Add new question button
      const addBtn = document.createElement('div')
      addBtn.className = 'add-question-btn'
      addBtn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        添加问题
      `
      addBtn.dataset.action = "click->interview-design#addNewQuestion"
      list.appendChild(addBtn)
      
      // Auto-resize all textareas
      setTimeout(() => {
        list.querySelectorAll('textarea').forEach(ta => {
          ta.style.height = 'auto'
          ta.style.height = ta.scrollHeight + 'px'
        })
      }, 0)
      
    } else {
      list.innerHTML = `
        <div style="text-align:center; margin-top:100px; color:#94a3b8; font-size: 14px; font-weight: 500;">
          <div style="font-size: 32px; margin-bottom: 12px; opacity: 0.8;">💡</div>
          <div style="line-height: 1.5;">等待提纲生成...</div>
        </div>`
    }
    
    // Show save button if we have questions
    // if (this.currentOutline.questions && this.currentOutline.questions.length > 0) {
    //   this.saveButtonTarget.style.display = 'block'
    // }
  }

  // Update goal
  updateGoal(event) {
    this.currentOutline.goal = event.target.value
    this.hasUnsavedChanges = true
  }

  // Update question text
  updateQuestionText(event) {
    const index = parseInt(event.target.dataset.index)
    if (this.currentOutline.questions[index]) {
      this.currentOutline.questions[index].text = event.target.value
      this.hasUnsavedChanges = true
    }
  }

  // Update question strategy
  updateQuestionStrategy(event) {
    const index = parseInt(event.target.dataset.index)
    if (this.currentOutline.questions[index]) {
      this.currentOutline.questions[index].strategy = event.target.value
      this.hasUnsavedChanges = true
    }
  }

  // Update question follow-up count
  updateQuestionFollowUp(event) {
    const index = parseInt(event.target.dataset.index)
    if (this.currentOutline.questions[index]) {
      this.currentOutline.questions[index].followUpCount = parseInt(event.target.value) || 0
      this.hasUnsavedChanges = true
    }
  }

  // Add new question
  addNewQuestion() {
    if (!this.currentOutline.questions) {
      this.currentOutline.questions = []
    }
    this.currentOutline.questions.push({
      text: "新问题",
      strategy: "请描述具体细节",
      followUpCount: this.followUpsPerMainValue
    })
    this.renderCanvas(null)
    this.hasUnsavedChanges = true
  }

  // Delete question
  deleteQuestion(event) {
    const index = parseInt(event.target.closest('button').dataset.index)
    if (!this.currentOutline.questions) return
    this.currentOutline.questions.splice(index, 1)
    this.renderCanvas(null)
    this.hasUnsavedChanges = true
  }

  // Move question up
  moveQuestionUp(event) {
    const index = parseInt(event.target.closest('button').dataset.index)
    if (index === 0) return
    const questions = this.currentOutline.questions
    ;[questions[index], questions[index - 1]] = [questions[index - 1], questions[index]]
    this.renderCanvas(null)
    this.hasUnsavedChanges = true
  }

  // Move question down
  moveQuestionDown(event) {
    const index = parseInt(event.target.closest('button').dataset.index)
    const questions = this.currentOutline.questions
    if (index >= questions.length - 1) return
    ;[questions[index], questions[index + 1]] = [questions[index + 1], questions[index]]
    this.renderCanvas(null)
    this.hasUnsavedChanges = true
  }

  // Resolve suggestion
  resolveSuggestion(event) {
    const index = parseInt(event.target.dataset.index)
    const isAccepted = event.target.dataset.accept === 'true'
    const q = this.currentOutline.questions[index]
    if (!q || !q.suggestion) return
    
    if (isAccepted) {
      const record = {
        timestamp: new Date().toLocaleString(),
        questionIndex: index,
        originalText: q.text,
        suggestion: JSON.parse(JSON.stringify(q.suggestion))
      }
      this.acceptedSuggestions.push(record)
      
      if (q.suggestion.action === 'delete') {
        this.currentOutline.questions.splice(index, 1)
        this.renderCanvas(this.currentOutline)
        return
      } else {
        if (q.suggestion.new_text) {
          q.text = q.suggestion.new_text.trim()
        } else if (q.suggestion.text) {
          q.text = this.getCleanedSuggestionText(q.suggestion.text)
        }
        
        if (q.suggestion.new_strategy) {
          q.strategy = q.suggestion.new_strategy.trim()
        } else if (q.suggestion.strategy) {
          q.strategy = this.getCleanedSuggestionText(q.suggestion.strategy)
        }
      }
    }
    delete q.suggestion
    this.renderCanvas(this.currentOutline)
    this.hasUnsavedChanges = true
  }

  // Resolve goal suggestion
  resolveGoalSuggestion(event) {
    const accepted = event.target.dataset.accept === 'true'
    if (!this.currentOutline.goalSuggestion) return

    if (accepted) {
      this.acceptedSuggestions.push({
        timestamp: new Date().toLocaleString(),
        type: 'goal',
        originalGoal: this.currentOutline.goal,
        newGoal: this.currentOutline.goalSuggestion.new_goal,
        reason: this.currentOutline.goalSuggestion.reason
      })
      this.currentOutline.goal = this.currentOutline.goalSuggestion.new_goal
      this.goalInputTarget.value = this.currentOutline.goal
      this.goalInputTarget.style.height = 'auto'
      this.goalInputTarget.style.height = this.goalInputTarget.scrollHeight + 'px'
    }

    delete this.currentOutline.goalSuggestion
    this.renderCanvas(this.currentOutline)
    this.hasUnsavedChanges = true
  }

  // Resolve recruitment suggestion
  resolveRecruitmentSuggestion(event) {
    const accepted = event.target.dataset.accept === 'true'
    if (!this.currentOutline.recruitmentSuggestion) return

    if (accepted) {
      const sugg = this.currentOutline.recruitmentSuggestion
      if (!this.currentOutline.recruitment) this.currentOutline.recruitment = {}
      if (sugg.persona) this.currentOutline.recruitment.persona = sugg.persona
      if (sugg.channels) this.currentOutline.recruitment.channels = sugg.channels
      if (sugg.quota) this.currentOutline.recruitment.quota = sugg.quota
    }

    delete this.currentOutline.recruitmentSuggestion
    this.renderCanvas(this.currentOutline)
    this.hasUnsavedChanges = true
  }

  // Switch tabs (triggered by button click)
  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    this._applyTab(tab)
  }

  // Internal tab switch (can be called programmatically)
  _applyTab(tab) {
    const tabOutlineBtn = this.element.querySelector('#tab-outline')
    const tabRecruitBtn = this.element.querySelector('#tab-recruit')
    const tabGuideBtn = this.element.querySelector('#tab-guide')
    const tabPersonaBtn = this.element.querySelector('#tab-persona')
    if (tabOutlineBtn) tabOutlineBtn.classList.toggle('active', tab === 'outline')
    if (tabRecruitBtn) tabRecruitBtn.classList.toggle('active', tab === 'recruit')
    if (tabGuideBtn) tabGuideBtn.classList.toggle('active', tab === 'guide')
    if (tabPersonaBtn) tabPersonaBtn.classList.toggle('active', tab === 'persona')
    if (this.hasOutlineTabContentTarget) {
      this.outlineTabContentTarget.style.display = tab === 'outline' ? 'block' : 'none'
    }
    if (this.hasRecruitTabContentTarget) {
      this.recruitTabContentTarget.style.display = tab === 'recruit' ? 'block' : 'none'
    }
    if (this.hasGuideTabContentTarget) {
      this.guideTabContentTarget.style.display = tab === 'guide' ? 'block' : 'none'
    }
    if (this.hasPersonaTabContentTarget) {
      this.personaTabContentTarget.style.display = tab === 'persona' ? 'block' : 'none'
    }
    // Resize recruit textareas now that they are visible
    if (tab === 'recruit') {
      setTimeout(() => {
        if (this.hasRecruitPersonaTarget) {
          this.recruitPersonaTarget.style.height = 'auto'
          this.recruitPersonaTarget.style.height = this.recruitPersonaTarget.scrollHeight + 'px'
        }
        if (this.hasRecruitChannelsTarget) {
          this.recruitChannelsTarget.style.height = 'auto'
          this.recruitChannelsTarget.style.height = this.recruitChannelsTarget.scrollHeight + 'px'
        }
      }, 10)
    }
  }

  // Update recruitment persona
  updateRecruitPersona(event) {
    if (!this.currentOutline.recruitment) this.currentOutline.recruitment = {}
    this.currentOutline.recruitment.persona = event.target.value
    this.hasUnsavedChanges = true
  }

  // Update recruitment channels
  updateRecruitChannels(event) {
    if (!this.currentOutline.recruitment) this.currentOutline.recruitment = {}
    this.currentOutline.recruitment.channels = event.target.value.split('\n').map(v => v.trim()).filter(v => v)
    this.hasUnsavedChanges = true
  }

  // Update basic quota entry
  updateQuotaBasic(event) {
    const index = parseInt(event.target.dataset.index)
    const field = event.target.dataset.field
    if (!this.currentOutline.recruitment?.quota?.basic?.[index]) return
    this.currentOutline.recruitment.quota.basic[index][field] = field === 'count' ? parseInt(event.target.value) || 0 : event.target.value
    this.hasUnsavedChanges = true
  }

  // Update pro quota entry
  updateQuotaPro(event) {
    const dimIdx = parseInt(event.target.dataset.dimIndex)
    const segIdx = parseInt(event.target.dataset.segIndex)
    const field = event.target.dataset.field
    if (!this.currentOutline.recruitment?.quota?.pro?.[dimIdx]?.segments?.[segIdx]) return
    this.currentOutline.recruitment.quota.pro[dimIdx].segments[segIdx][field] = field === 'count' ? parseInt(event.target.value) || 0 : event.target.value
    this.hasUnsavedChanges = true
  }

  // Helper methods
  normalizeRecruitmentData(recruitment) {
    if (!recruitment || typeof recruitment !== 'object') return null

    // Check if it's already in the correct format
    if (recruitment.persona && recruitment.channels && recruitment.quota &&
        recruitment.quota.basic && recruitment.quota.pro) {
      return recruitment
    }

    // Transform AI's creative structure to our expected structure
    const normalized = {
      persona: recruitment.persona || recruitment.targetPersona || "",
      channels: recruitment.channels || [],
      quota: { basic: [], pro: [] }
    }

    // Handle quota structure
    if (recruitment.quota) {
      if (recruitment.quota.basic && recruitment.quota.pro) {
        // Already correct format
        normalized.quota = recruitment.quota
      } else {
        // Try to extract quota arrays from various possible structures
        Object.entries(recruitment.quota).forEach(([key, value]) => {
          if (!Array.isArray(value)) return

          // Check if this looks like a dimension-based structure (pro format)
          if (value.length > 0 && value[0].name && value[0].count) {
            // This is a simple array - treat as a dimension
            normalized.quota.pro.push({
              dimension: key,
              segments: value.map(item => ({
                name: item.name,
                count: item.count || 0
              }))
            })
          }
        })
      }
    }

    return normalized
  }

  formatMethodName(key) {
    if (!key) return ""
    return key.includes('：') ? key.split('：').slice(1).join('：') : key
  }

  getCleanedSuggestionText(rawText) {
    if (!rawText) return ""
    let text = rawText.trim()
    const quoteMatch = text.match(/[""'](.+?)[""']/)
    if (quoteMatch) return quoteMatch[1]
    
    const prefixes = ["新问题：", "新问题:", "建议改为：", "建议改为:", "修改为：", "修改为:"]
    for (let pref of prefixes) {
      if (text.startsWith(pref)) {
        return text.substring(pref.length).trim()
      }
    }
    return text
  }

  clearAllSuggestions(outline) {
    if (!outline || !outline.questions) return outline
    const cleaned = JSON.parse(JSON.stringify(outline))
    if (cleaned.questions) {
      cleaned.questions.forEach(q => {
        if (q.suggestion) delete q.suggestion
      })
    }
    if (cleaned.goalSuggestion) delete cleaned.goalSuggestion
    if (cleaned.recruitmentSuggestion) delete cleaned.recruitmentSuggestion
    return cleaned
  }

  getUpdateContext(currentJson) {
    const currentCount = currentJson.questions ? currentJson.questions.length : 0
    let strategy = ""
    
    if (currentCount === this.targetMainQCount) {
      strategy = "数量已达标：严禁新增或删除，只能通过 suggestion 字段针对已有问题提出修改建议。"
    } else if (currentCount < this.targetMainQCount) {
      strategy = `数量不足（差 ${this.targetMainQCount - currentCount} 个）：请在 questions 数组中直接新增问题对象补足，或根据情况在 suggestion 字段提出 "text" 修改建议。`
    } else {
      strategy = `数量超标（多出 ${currentCount - this.targetMainQCount} 个）：必须对多余的问题在 suggestion 中标记 "action": "delete" 建议删除。同时可以根据情况在 suggestion 字段提出 "text" 修改建议。`
    }
    
    return `
【配置目标】：主问题数量应为 ${this.targetMainQCount}
【提纲现状】：已有 ${currentCount} 个。
【当前研究目标】：${currentJson.goal || "暂无"}
【当前判断阶段】：${currentJson.stage || "暂无"}
【当前推荐方法】：${currentJson.method || "暂无"}
【当前任务策略】：${strategy}
【当前数据结构】：
${JSON.stringify(currentJson, null, 2)}
`
  }

  getBasePersona() {
    return `你是一名资深用户研究员。你的核心产出是 <outline> JSON 数据，其中包含访谈提纲（questions）和招募计划（recruitment），以及诊断信息（stage/method）。`
  }

  getPromptDiscovery() {
    return `
【当前阶段：诊断需求】
你是资深用户研究员，正在通过对话了解项目背景以确定最佳研究方法。

🔍 **你需要逐步了解**：
- 业务阶段（早期探索/增长期/成熟迭代等）
- 已经做过的研究或已有认知
- 本次调研的核心假设或待验证问题
- 调研结果的决策场景

📤 **每一轮对话后必须输出以下内容（顺序固定）**：
1. **<diagnosis>** 标签：JSON，格式为：
   {
     "stage": "探索期" | "增长期" | "成熟期" | "待确定",
     "method": ["推荐方法1", "推荐方法2"],
     "confidence": 0.0-1.0,
     "gaps": "仍需补充的信息描述",
     "goal": "当前认知下的研究目标草稿"
   }
2. **<reply>** 标签：根据诊断结果，简要总结你的判断（推荐的研究方法和阶段），然后告诉用户"接下来请设置目标问题数量，我将为您生成完整提纲"。

📌 **方法选择参考**：请从以下列表中挑选最匹配的方法：
${JSON.stringify(Object.keys(this.modeRequirements))}

⚠️ **重要**：本阶段不要输出 <outline> 标签，只需要输出诊断信息和回复。提纲将在用户确认问题数量后生成。

🚫 严禁只输出标签而不说话。
`
  }

  getPromptInitial() {
    return `
【当前阶段：初次构建】
1. 用户已提供背景，请根据 [执行指令] 直接生成一份完整的研究设计（包含研究目标、诊断信息、招募计划和访谈提纲）。
2. **必须**输出 <outline> JSON，且主问题数量必须严格等于配置数量。outline中第一个问题要相对简单易答，起到预热作用。每个text字段是一个独立单一问题，避免过度复杂冗长。
3. JSON 内部必须严格包含以下结构：
   - "goal": 研究目标。
   - "stage": 判断用户所处的业务阶段（如"早期探索"、"需求验证"、"产品上市前"、"运营优化"等）。
   - "method": 基于阶段和背景推荐的研究方法（如"深度访谈"、"焦点小组"、"概念测试"等）。
   - "recruitment": 招募计划对象，必须包含：
       "persona": 目标画像与具体招募标准，需说明排除人群，分行结构化展示，使用 \\n 换行 (字符串)。
       "quota": 配额对象，包含：
           - "basic": 初步探索版数组，格式为 [{"profile": "单一特征", "count": 配额占比（合计应为100）}]。
           - "pro": 专业完整版多维度配额数组，格式为 [{"dimension": "维度名称", "segments": [{"name": "分类名称", "count": 配额占比}]}]，同一维度各项合计应为100。
       "channels": 招募渠道推荐 (字符串数组)。
   - "questions": 提纲问题数组，每个问题**必须**包含 "text" (问题正文) 和 "strategy" (追问/挖掘策略)，两个字段缺一不可。
4. 你的输出结构必须包含：
   <outline>{ "goal": "...", "stage": "...", "method": "...", "recruitment": { "persona": "...", "quota": { "basic": [...], "pro": [...] }, "channels": [...] }, "questions": [ { "text": "...", "strategy": "..." } ] }</outline>
   <reply>根据研究模式要求，提出一个**关于研究项目本身**的后续问题，以帮助完善提纲设计。
    首先描述下您认为为了完善提纲还需要了解什么，然后给出问题。问题应围绕业务背景、研究目的、业务假设、需验证的维度、需要收集的信息等。
    每次只问一个问题，要具体清晰，不要长篇大论</reply>

【特别注意】：输出 <outline> 和 <reply> 两个标签即可。<reply> 中简要评价并询问一个具体细节。严禁只输出标签而不说话。
`
  }

  getPromptIterate() {
    const hasRecruitment = this.currentOutline.recruitment?.persona &&
                           this.currentOutline.recruitment?.channels?.length > 0

    const recruitmentInstruction = hasRecruitment
      ? `招募计划已存在，无需每次返回。如用户提及画像/配额/渠道修改，在根节点添加 "recruitmentSuggestion": { "persona": "...", "quota": { "basic": [...], "pro": [...] }, "channels": [...], "reason": "..." }。不涉及修改则不输出此字段，也不输出 recruitment 字段。`
      : `当前缺少招募计划，请在 outline 中包含 "recruitment"：
  { "persona": "目标画像（字符串，用\\n换行）", "quota": { "basic": [{"profile":"...", "count": 占比}], "pro": [{"dimension":"维度", "segments":[{"name":"分类", "count": 占比}]}] }, "channels": ["渠道1", "渠道2"] }`

    return `
【当前阶段：审核与修补】
你是"提纲审核员"，针对用户的修改意见调整提纲。

**核心规则**：
1. 返回**完整提纲 JSON**（包含所有问题，含未修改的）
2. 每个问题**必须**包含 "text"（问题正文）和 "strategy"（追问策略），缺一不可
3. JSON 必须包含 "goal", "stage", "method", "questions" 字段
4. 第一个问题应简单易答起到预热作用，每个 text 是独立单一问题

**修改原则**：仅在问题有逻辑缺陷、重复、遗漏关键维度、或与用户目标不符时才修改。每次修改必须附 reason 字段。

**修改策略**（基于数量对比）：
- 数量不足 → 在 questions 数组中新增问题对象（含 text 和 strategy）
- 数量超标 → 给多余问题添加 "suggestion": { "action": "delete", "reason": "..." }
- 数量达标 → 仅对需修改的问题添加 "suggestion": { "new_text": "...", "new_strategy": "...", "reason": "..." }

**研究目标修改**（仅在必要时）：
添加 "goalSuggestion": { "new_goal": "...", "reason": "..." }

**招募计划**：
${recruitmentInstruction}

**输出格式（仅两个标签）**：
<outline>完整提纲 JSON</outline>
<reply>简要评价 + 一个具体的后续问题</reply>

严禁只输出标签而不说话。`
  }

  // Save outline to database
  saveOutline(event) {
    event.preventDefault()

    if (!confirm('确定要保存当前提纲到数据库吗？这将替换现有的问题。')) return

    this.saveButtonTarget.disabled = true
    this.saveButtonTarget.innerHTML = '<i class="fas fa-circle-notch fa-spin me-1"></i>保存中...'

    this.subscription.send({
      action: "save_outline",
      outline: this.currentOutline,
      design_config: {
        method: this.selectedMethod,
        target_question_count: this.targetMainQCount,
        config_context: this.configContext,
        stage: this.currentOutline.stage || "",
        diagnosis_method: this.currentOutline.method || "",
        persona_guide: this.currentOutline.personaGuide || null
      },
      return_test_url: false
    })
  }

  // Save outline and create interview set for testing
  saveAndTest(event) {
    event.preventDefault()

    if (!confirm('确定要保存当前提纲并创建测试访谈吗？')) return

    this.saveAndTestButtonTarget.disabled = true
    this.saveAndTestButtonTarget.innerHTML = '<i class="fas fa-circle-notch fa-spin me-1"></i>保存中...'

    this.subscription.send({
      action: "save_outline",
      outline: this.currentOutline,
      design_config: {
        method: this.selectedMethod,
        target_question_count: this.targetMainQCount,
        config_context: this.configContext,
        stage: this.currentOutline.stage || "",
        diagnosis_method: this.currentOutline.method || "",
        persona_guide: this.currentOutline.personaGuide || null
      },
      return_test_url: true
    })
  }

  // Handle save completion
  handleSaveComplete(data) {
    this.saveButtonTarget.disabled = false
    this.saveButtonTarget.innerHTML = '<i class="fas fa-save me-1"></i>保存'

    if (this.hasSaveAndTestButtonTarget) {
      this.saveAndTestButtonTarget.disabled = false
      this.saveAndTestButtonTarget.innerHTML = '<i class="fas fa-play-circle me-1"></i>保存并测试访谈'
    }

    // Reset unsaved changes flag after successful save
    this.hasUnsavedChanges = false

    this.renderChat({
      role: "assistant",
      content: `
        <div style="background: #d1e7dd; border: 1px solid #badbcc; border-radius: 8px; padding: 12px; margin: 8px 0; color: #0f5132;">
          <i class="fas fa-check-circle" style="margin-right: 8px;"></i>
          成功保存 ${data.questions_count} 个问题到数据库
        </div>
      `
    })

    // If there's a redirect URL (from save and test), open in new tab
    if (data.redirect_url) {
      window.open(data.redirect_url, '_blank')
    }
  }

  // --- AI 提问质检员 ---
  checkQuestion() {
    const inputEl = this.element.querySelector('#inspector-input')
    const text = inputEl.value.trim()
    if (!text) return

    const btn = this.element.querySelector('#inspector-btn')
    const resDiv = this.inspectorResultTarget

    // UI loading state
    btn.disabled = true
    btn.innerText = "质检中..."
    resDiv.style.display = "block"
    resDiv.innerHTML = `<div class="typing-indicator" style="padding: 10px;"><span></span><span></span><span></span></div>`

    // Send via ActionCable
    this.subscription.send({
      action: "inspect_question",
      question: text
    })
  }

  handleInspectQuestionComplete(data) {
    const btn = this.element.querySelector('#inspector-btn')
    const resDiv = this.inspectorResultTarget

    if (data.reply) {
      let replyHtml = data.reply.replace(/<think>[\s\S]*?<\/think>/, '').trim()
      resDiv.innerHTML = `<div class="inspector-feedback">${replyHtml}</div>`
    } else if (data.error) {
      resDiv.innerHTML = `<div style="color: var(--danger-color); padding: 10px; font-size: 13px;">❌ ${data.error}</div>`
    }

    if (btn) {
      btn.disabled = false
      btn.innerText = "开始鉴定"
    }
  }

  // --- Persona Guide ---
  renderPersonaGuide() {
    if (!this.hasPersonaGuideWrapperTarget) return
    const wrapper = this.personaGuideWrapperTarget
    const guide = this.currentOutline.personaGuide

    if (!guide) {
      wrapper.innerHTML = `
        <div style="text-align:center; padding:40px; color: var(--text-secondary);">
          <div style="font-size:32px; margin-bottom:12px;">🎯</div>
          执行要点将在提纲生成后自动生成...
        </div>
      `
      return
    }

    let html = ''

    // Invite script
    if (guide.invite_script) {
      html += `
        <div class="persona-guide-section">
          <div class="guide-title">📞 邀约话术</div>
          <div class="persona-guide-item">${this.escapeHtml(guide.invite_script).replace(/\n/g, '<br>')}</div>
        </div>
      `
    }

    // Warmup topics
    if (guide.warmup_topics && guide.warmup_topics.length > 0) {
      html += `
        <div class="persona-guide-section">
          <div class="guide-title">☕ 暖场话题</div>
          ${guide.warmup_topics.map(t => `<div class="persona-guide-item">• ${this.escapeHtml(t)}</div>`).join('')}
        </div>
      `
    }

    // Cautions
    if (guide.cautions && guide.cautions.length > 0) {
      html += `
        <div class="persona-guide-section">
          <div class="guide-title">⚠️ 特别注意事项</div>
          ${guide.cautions.map(c => `<div class="persona-guide-item">• ${this.escapeHtml(c)}</div>`).join('')}
        </div>
      `
    }

    // Contingencies
    if (guide.contingencies && guide.contingencies.length > 0) {
      html += `
        <div class="persona-guide-section">
          <div class="guide-title">🚨 突发状况应对</div>
          ${guide.contingencies.map(c => `
            <div class="contingency-item">
              <div class="situation">💥 ${this.escapeHtml(c.situation)}</div>
              <div class="response">→ ${this.escapeHtml(c.response)}</div>
            </div>
          `).join('')}
        </div>
      `
    }

    wrapper.innerHTML = html || `
      <div style="text-align:center; padding:40px; color: var(--text-secondary);">
        <div style="font-size:32px; margin-bottom:12px;">🎯</div>
        执行要点将在提纲生成后自动生成...
      </div>
    `
  }

  handlePersonaGuideComplete(data) {
    if (data.guide) {
      this.currentOutline.personaGuide = data.guide
      this.renderPersonaGuide()
    } else if (data.error) {
      if (this.hasPersonaGuideWrapperTarget) {
        this.personaGuideWrapperTarget.innerHTML = `
          <div style="text-align:center; padding:40px; color: var(--danger-color);">
            <div style="font-size:32px; margin-bottom:12px;">❌</div>
            生成失败：${data.error}
          </div>
        `
      }
    }
  }

  // Request persona guide generation via ActionCable
  generatePersonaGuide() {
    if (!this.currentOutline.recruitment?.persona) return
    if (!this.currentOutline.goal) return

    // Show loading spinner
    if (this.hasPersonaGuideWrapperTarget) {
      this.personaGuideWrapperTarget.innerHTML = `
        <div style="text-align:center; padding:40px; color: var(--text-secondary);">
          <div class="typing-indicator" style="margin: 0 auto 12px; justify-content: center;"><span></span><span></span><span></span></div>
          正在生成执行要点...
        </div>
      `
    }

    this.subscription.send({
      action: "generate_persona_guide",
      goal: this.currentOutline.goal,
      persona: this.currentOutline.recruitment.persona,
      method: this.currentOutline.method || this.selectedMethod || ""
    })
  }
}
